import React, { useState, useRef, useEffect } from 'react';
import Plot from 'react-plotly.js';
import 'leaflet-editable';
import { CRS } from 'leaflet';
import { GeoJSON } from 'react-leaflet';
import { useSelector } from 'react-redux';
import * as turf from '@turf/turf';

import { selectGreenhouse, useGreenhouseActions } from 'state';
import { themes } from 'styles';

import {
  StyledMapContainer,
  DetailInfo,
  MapBlock,
  SidebarList,
  SidebarItem,
  SidebarKey,
  SidebarValue,
  ColorBlock,
  Popup,
  CloseButton,
  LegendBlock,
  Legend,
  Gradient
} from './Map.styles';

const selectedFeatureColor = themes.themeDefault.colors.nature.n4;

export const Map = ({ data, backgroundData, fullData, mapField, ...props }) => {
  const { selectedZone } = useSelector(selectGreenhouse);
  const { setSelectedZone } = useGreenhouseActions();
  const backgroundGeoJsonRef = useRef(null);
  const dataGeoJsonRef = useRef(null);
  const [map, setMap] = useState(null);
  const [key, setKey] = useState(0);
  const [sidebarContent, setSidebarContent] = useState(null);
  const [selectedBed, setSelecctedBed] = useState(null);
  const [sidebarChart, setSidebarChart] = useState(null);
  const featureColors = useRef({});
  const geojsonEvents = {
    add: e => map.fitBounds(e.target.getBounds())
  };

  const interpolateColorCanopy = percent => {
    const clamped = Math.max(0, Math.min(100, percent));
    let red, green;
    if (clamped <= 50) {
      red = 255;
      green = Math.round(255 * (clamped / 50));
    } else {
      red = Math.round(255 * ((100 - clamped) / 50));
      green = 255;
    }
    return `rgb(${red}, ${green}, 0)`;
  };

  const interpolateColorInduction = value => {
    const clamped = Math.max(0, Math.min(2, value));
    let red, green;
    if (clamped === 0) {
      red = 0;
      green = 255;
    } else if (clamped === 2) {
      red = 255;
      green = 0;
    } else {
      red = 255;
      green = 255;
    }
    return `rgb(${red}, ${green}, 0)`;
  };

  const interpolateColorYellowLeaves = value => {
    const clamped = Math.max(0, value);
    let red, green;
    if (clamped < 40) {
      red = 0;
      green = 255;
    } else if (clamped > 100) {
      red = 255;
      green = 0;
    } else {
      red = 255;
      green = 255;
    }
    return `rgb(${red}, ${green}, 0)`;
  };

  const interpolateColorBrownLeaves = value => {
    const clamped = Math.max(0, value);
    let red, green;
    if (clamped < 20) {
      red = 0;
      green = 255;
    } else if (clamped > 60) {
      red = 255;
      green = 0;
    } else {
      red = 255;
      green = 255;
    }
    return `rgb(${red}, ${green}, 0)`;
  };

  const selectInterpolationType = {
    canopy_percentage: interpolateColorCanopy,
    induction_per_m2: interpolateColorInduction,
    yellow_leaves_per_m2: interpolateColorYellowLeaves,
    brown_leaves_per_m2: interpolateColorBrownLeaves
  };
  const selectLegendText = {
    canopy_percentage: (
      <>
        <p>Canopy Coverage Percentage:</p>
        <p className='description'>
          {' '}
          <Gradient></Gradient>100% 50% 0%
        </p>
      </>
    ),
    induction_per_m2: (
      <>
        <p>Induction per m²: </p>
        <p className='description'>
          {' '}
          <Gradient></Gradient>0 0-2 &gt;2
        </p>
      </>
    ),
    yellow_leaves_per_m2: (
      <>
        <p>Yellow Leaves per m²:</p>
        <p className='description'>
          {' '}
          <Gradient></Gradient>
          &lt;40 40-100 &gt;100
        </p>
      </>
    ),
    brown_leaves_per_m2: (
      <>
        <p>Brown Leaves per m²:</p>
        <p className='description'>
          {' '}
          <Gradient></Gradient>
          &lt;20 20-60 &gt;60
        </p>
      </>
    )
  };

  const plotLayout = {
    width: 230,
    height: 150,
    margin: { t: 20, b: 40, l: 20, r: 10 },
    font: {
      family: 'Arial'
    },
    showlegend: true,
    legend: {
      orientation: 'h',
      x: 0.5,
      xanchor: 'center',
      y: 1.1,
      yanchor: 'bottom'
    }
  };

  const geojsonFeatureHandler = (feature, layer) => {
    layer.on({
      click: ({ target }) => {
        if (!target.feature.properties.UUID) return;
        setSelectedZone(target.feature.properties.UUID);
        setSelecctedBed(target.feature.properties.bed);
        const clickedFeature = target.feature;
        let intersectingFeatures = [];

        if (data) {
          data.forEach(dataFeature => {
            try {
              const clickedPolygon = turf.polygon(clickedFeature.geometry.coordinates);
              const bgPolygon = turf.polygon(dataFeature.geometry.coordinates);

              if (turf.intersect(turf.featureCollection([clickedPolygon, bgPolygon]))) {
                intersectingFeatures.push(dataFeature.properties);
              }
            } catch (error) {
              console.error('Error checking intersection:', error);
            }
          });
        }

        setSidebarContent({
          ...clickedFeature.properties,
          ...Object.assign({}, ...intersectingFeatures)
        });
      }
    });
    layer.setStyle({
      color: layer.feature.properties.color,
      fillColor: layer.feature.properties.color,
      fillOpacity: 0.1,
      opacity: 0.15,
      weight: 1
    });
  };
  const dataFeatureHandler = (feature, layer) => {
    const polygonId = feature.properties.polygon_id;
    if (!polygonId) return;

    featureColors.current[polygonId] = selectInterpolationType[mapField](
      feature.properties[mapField]
    );
    layer.setStyle({
      color: featureColors.current[polygonId],
      fillColor: featureColors.current[polygonId],
      fillOpacity: 1,
      opacity: 1,
      weight: 0
    });
  };

  useEffect(() => setKey(x => x + 1), [data]);
  useEffect(() => {
    if (fullData !== undefined) {
      const chartData = fullData
        .filter(
          item =>
            item.properties.polygon_id === selectedZone &&
            item.properties.bed === selectedBed
        )
        .map(item => item.properties);
      const sortedChartData = [...chartData].sort((a, b) => {
        return new Date(a.mission_date) - new Date(b.mission_date);
      });
      setSidebarChart(sortedChartData);
    }
  }, [fullData, selectedZone, selectedBed]);
  useEffect(() => {
    if (!backgroundGeoJsonRef.current || !map) return;

    backgroundGeoJsonRef.current.eachLayer(layer => {
      const uuid = layer.feature.properties.UUID;
      if (!uuid) return;

      if (uuid === selectedZone) {
        layer.setStyle({
          color: selectedFeatureColor,
          opacity: 0.8,
          weight: 2
        });
        layer.bringToFront();
      } else {
        layer.setStyle({
          color: layer.feature.properties.color,
          fillColor: layer.feature.properties.color,
          fillOpacity: 0.1,
          opacity: 0.15,
          weight: 1
        });
      }
    });
  }, [selectedZone, map]);

  useEffect(() => {
    if (
      !backgroundGeoJsonRef.current ||
      !backgroundGeoJsonRef.current.leafletElement ||
      !map ||
      !backgroundData
    )
      return;

    const geojsonLayer = backgroundGeoJsonRef.current.leafletElement;
    const bounds = geojsonLayer.getBounds();
    const center = bounds.getCenter();
    map.setView(center, map.getZoom());
  }, [backgroundData, map]);

  return (
    <MapBlock>
      <StyledMapContainer
        editable={false}
        center={[50, 50]}
        zoom={0}
        scrollWheelZoom={true}
        zoomControl={true}
        whenCreated={setMap}
        crs={CRS.Simple}
        minZoom={0}
        maxZoom={8}
        {...props}
      >
        {map && data && (
          <GeoJSON
            key={key}
            data={data}
            ref={dataGeoJsonRef}
            eventHandlers={geojsonEvents}
            onEachFeature={dataFeatureHandler}
          />
        )}
        {map && backgroundData && (
          <GeoJSON
            key={`bg-${key}`}
            data={backgroundData}
            // style={getBackgroundLayerStyle}
            ref={backgroundGeoJsonRef}
            onEachFeature={geojsonFeatureHandler}
          />
        )}
      </StyledMapContainer>
      <Legend>{selectLegendText[mapField]}</Legend>
      <DetailInfo>
        {sidebarContent && (
          <Popup>
            <CloseButton icon='Cross' onClick={() => setSidebarContent(null)} />
            <SidebarList>
              {Object.entries(sidebarContent)
                .filter(([key]) =>
                  [
                    'bed',
                    'variety_code',
                    'mission_date',
                    'planting_date',
                    'mission_name',
                    'variety_area',
                    'variety_name',
                    'canopy_percentage',
                    'induction_per_m2',
                    'yellow_leaves_per_m2',
                    'brown_leaves_per_m2'
                  ].includes(key)
                )
                .map(([key, value]) => (
                  <SidebarItem key={key}>
                    <SidebarKey>{key.replace(/_/g, ' ')}:</SidebarKey>
                    {key === 'color' ? (
                      <SidebarValue>
                        <ColorBlock style={{ backgroundColor: value }} title={value} />
                      </SidebarValue>
                    ) : (
                      <SidebarValue>
                        {Array.isArray(value)
                          ? value
                              .map(v =>
                                typeof v === 'number' && !Number.isInteger(v)
                                  ? v.toFixed(2)
                                  : v
                              )
                              .join(', ')
                          : typeof value === 'number' && !Number.isInteger(value)
                          ? value.toFixed(2)
                          : value}
                      </SidebarValue>
                    )}
                  </SidebarItem>
                ))}
              {sidebarChart && (
                <>
                  <SidebarItem>
                    <Plot
                      data={[
                        {
                          y: sidebarChart.map(item => item.canopy_percentage.toFixed(2)),
                          x: sidebarChart.map(item => item.mission_date),
                          type: 'scatter',
                          mode: 'lines+markers',
                          name: 'Canopy Coverage %'
                        }
                      ]}
                      layout={plotLayout}
                    />
                    <Plot
                      data={[
                        {
                          y: sidebarChart.map(item => item.induction_per_m2.toFixed(2)),
                          x: sidebarChart.map(item => item.mission_date),
                          type: 'scatter',
                          mode: 'lines+markers',
                          name: 'Induction per m²'
                        }
                      ]}
                      layout={plotLayout}
                    />
                  </SidebarItem>
                  <SidebarItem>
                    <Plot
                      data={[
                        {
                          y: sidebarChart.map(item =>
                            item.yellow_leaves_per_m2.toFixed(2)
                          ),
                          x: sidebarChart.map(item => item.mission_date),
                          type: 'scatter',
                          mode: 'lines+markers',
                          name: 'Yellow Leaves per m²'
                        }
                      ]}
                      layout={plotLayout}
                    />
                    <Plot
                      data={[
                        {
                          y: sidebarChart.map(item =>
                            item.brown_leaves_per_m2.toFixed(2)
                          ),
                          x: sidebarChart.map(item => item.mission_date),
                          type: 'scatter',
                          mode: 'lines+markers',
                          name: 'Brown Leaves per m²'
                        }
                      ]}
                      layout={plotLayout}
                    />
                  </SidebarItem>
                </>
              )}
            </SidebarList>
          </Popup>
        )}
      </DetailInfo>
    </MapBlock>
  );
};
